/*
 * PROD-7828 R10 — audit-trail browser: response shaping and error routing. Pure; the tab renders
 * what these return. Filter values come from /audit/facets (R10-C12), never a hard-coded list.
 */
import { EM_DASH, api_error, is_permission_error, month_label } from '../format';

// "pass_keep_unused" -> "Pass keep unused": sentence case, the code itself stays in the title.
export const action_label = (action) => {
  const words = String(action || '')
    .split('_')
    .filter(Boolean)
    .join(' ');
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : EM_DASH;
};

// A value as Ops should read it: missing -> em dash, blank text says so, objects/arrays as JSON.
export const audit_value_text = (v) => {
  if (v === null || v === undefined) return EM_DASH;
  if (v === '') return '(blank)';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const change_view = (c) => ({
  field: String((c && c.field) || ''),
  old_text: audit_value_text(c && c.old),
  new_text: audit_value_text(c && c.new)
});

const actor_text = (actor) => {
  if (!actor || typeof actor !== 'object') return EM_DASH;
  return actor.name || actor.id || EM_DASH;
};

export const audit_row_view = (r) => ({
  id: r.id,
  when: r.created_at_ist || EM_DASH,
  entity_label: r.entity_label || r.entity_type || EM_DASH,
  entity_id: r.entity_id || '',
  action: action_label(r.action),
  actor: actor_text(r.actor),
  teacher_name: r.teacher_name || '',
  teacher_uuid: r.teacher_uuid || '',
  // NULL pay_month rows (profile changes, jobs) are listed under the IST month they happened in.
  month: r.pay_month ? month_label(r.pay_month) : EM_DASH,
  reason: r.reason || '',
  changes: Array.isArray(r.changes) ? r.changes.map(change_view) : []
});

export const audit_page_view = (data) => {
  const d = data || {};
  const next_before = Number(d.next_before_id);
  const next_off = Number(d.next_offset);
  return {
    rows: (Array.isArray(d.rows) ? d.rows : []).map(audit_row_view),
    // The default (keyset) order pages by next_before_id; a column sort pages by next_offset instead
    // (reports.AuditListResponse) — the two are never both set.
    next_before_id: Number.isFinite(next_before) && next_before > 0 ? next_before : null,
    next_offset: Number.isFinite(next_off) && next_off > 0 ? next_off : null
  };
};

// "Load older" appends; an id already listed (a double press) is not listed twice.
export const append_page = (rows, page) => {
  const seen = new Set(rows.map((r) => r.id));
  return rows.concat(page.rows.filter((r) => !seen.has(r.id)));
};

const facet_options = (list, to_option) => (Array.isArray(list) ? list : []).map(to_option);

export const audit_facets_view = (data) => {
  const d = data || {};
  return {
    entity_types: facet_options(d.entity_types, (f) => ({
      value: f.value,
      label: `${f.label || f.value} (${f.count})`
    })),
    actions: facet_options(d.actions, (f) => ({
      value: f.value,
      label: `${action_label(f.label || f.value)} (${f.count})`
    })),
    actors: facet_options(d.actors, (a) => ({ value: a.id, label: a.name || a.id }))
  };
};

// The name for a deep-linked teacher, known only once a row of theirs comes back.
export const teacher_name_from_rows = (rows, teacher_uuid) => {
  const hit = rows.find((r) => r.teacher_uuid === teacher_uuid && r.teacher_name);
  return hit ? hit.teacher_name : '';
};

/*
 * Every code an audit read can return (reports_enums.json error_codes minus the report-only ones).
 * FORBIDDEN goes to the shell's no-permission page; the rest render inside the tab, with the
 * server's sentence when it sent one (CD-144) and this text otherwise.
 */
export const AUDIT_ERROR_TEXT = {
  INVALID_REQUEST: 'The filters could not be read. Reset them and try again.',
  INVALID_PAY_MONTH: 'The month must look like 2026-09.',
  AUDIT_FILTER_REQUIRED: 'Choose a month or a teacher to see the audit trail.',
  INVALID_PAGINATION: 'Could not load older entries. Reload the list and try again.',
  TEACHER_NOT_FOUND: 'No teacher matches this link. Pick the teacher again.',
  BAD_SORT: 'Could not sort the audit trail that way. Reload the list and try again.',
  LOOKUP_FAILED: 'Something went wrong on the server. Try again.'
};
export const AUDIT_PERMISSION_CODES = ['FORBIDDEN'];

export const audit_error_view = (e, fallback) => {
  const { code, message } = api_error(e);
  if (is_permission_error(e) || AUDIT_PERMISSION_CODES.indexOf(code) !== -1) {
    return { kind: 'permission', code };
  }
  return { kind: 'inline', code, text: message || AUDIT_ERROR_TEXT[code] || fallback };
};
